<template>
    <v-stepper v-model='stp' vertical editable>
        <v-stepper-step 
            :complete='stp > 0' 
            step='0' 
            color='secondary' 
            dark 
            editable
            >
            Introduction
        </v-stepper-step>
        <v-stepper-content step='0'>
            <v-card flat>
                <v-card-text>
                    <v-row>
                        <v-col cols='9'>
                            <p>
                                This tool has been created as a means of creating bespoke <strong>A1-5 carbon targets</strong> for projects. 
                                Any output from this tool is there to inform projects on what they <strong>should be aiming to achieve</strong>, 
                                rather than what they are going to achieve. It is designed to be as minimal as possible, with limited input 
                                based on information that is likely to exist as part of any project brief.
                            </p>
                            <p>
                                The diagram below indicates when this tool should be used across project milestones, increasing in maturity from 
                                left to right, to generate embodied carbon targets and when design and assessment tools should be used to ensure 
                                that the actual project is meeting those targets. Of course, projects aren't always that simple. As a more general 
                                rule, this tool should be treated as a pre-design tool - allowing users to make informed decisions prior to any 
                                formal design taking place.
                            </p>
                        </v-col>
                        <v-col cols='9'>
                            <v-img src='@/assets/pdecc/calc-guide.png' contain></v-img>
                        </v-col>
                        <v-col cols='9'>
                            <p>
                                The tool prompts users to combine cards found in the 
                                <v-chip to='/library' pill small outlined color='secondary'>Typology library</v-chip> to generate targets for the 
                                key systems within the projecy. Currently this covers Structures and Building Envelope. To use the tool, follow 
                                the steps below and fill in the various fields when prompted. A summary is provided at the end of this process, 
                                which can be taken away as a screen shot.
                            </p>
                            <p>
                                <strong>NOTE:</strong> This tool currently uses typology cards developed for assessing commercial office buildings 
                                in the UK only. Using this tool to create a bespoke target for other building types & sectors, should be approached 
                                with caution.
                            </p>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-card flat>
                <v-card-actions>
                    <v-btn color='secondary' @click='stp = 1'>Get started!</v-btn>
                </v-card-actions>
            </v-card>
        </v-stepper-content>
        
        <v-stepper-step 
            :complete='stp > 1' 
            step='1' 
            color='secondary' 
            dark 
            editable
            >
            Project Information
        </v-stepper-step>
        <v-stepper-content step='1'>
            <v-card flat>
                <v-form
                    lazy-validation
                >
                    <v-container>
                        <v-row>
                            <v-col cols='12' sm='6'>
                                <v-text-field
                                    label = 'Project Name'
                                    single-line
                                    color='secondary'
                                    required
                                    v-model='projInfo.name' 
                                    :rules='[rules.exists]'                                
                                ></v-text-field>
                            </v-col>
                            <v-col cols='12' sm='6'>
                                <v-text-field
                                    label = 'Job Number'
                                    :rules='[rules.isNumber, rules.jn]'
                                    single-line
                                    color='secondary'
                                    required
                                    v-model='projInfo.jobNumber'
                                ></v-text-field>
                            </v-col>
                            <v-col cols='12' sm='6'>
                                <v-autocomplete
                                    label = 'Sector'
                                    single-line
                                    :items='sectors'
                                    color='secondary'
                                    item-color='secondary'
                                    required
                                    v-model='projInfo.sector'
                                ></v-autocomplete>
                            </v-col>
                            <v-col cols='12' sm='6'>
                                <v-autocomplete
                                    label = 'Type'
                                    single-line
                                    :items='projectTypes'
                                    color='secondary'
                                    item-color='secondary'
                                    required
                                    v-model='projInfo.type'
                                ></v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row class='my-2'>
                            <v-col cols='12' sm='6'>
                                <v-text-field
                                    label = 'Gross Internal Area'
                                    suffix = 'm2'
                                    color = 'secondary'
                                    :rules ='[rules.isNumber, rules.aboveZero, rules.exists]'
                                    required
                                    v-model='projInfo.gia'
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card>
            <v-card flat>
                <v-card-actions>
                    <v-btn text color='secondary' @click='stp = 2'>Continue</v-btn>
                    <v-btn text color='secondary' @click='stp = 0'>Back</v-btn>
                </v-card-actions>
            </v-card>
        </v-stepper-content>

        <v-stepper-step 
            :complete='stp > 2' 
            step='2' 
            color='secondary' 
            dark 
            editable
            >
            Building Structure
        </v-stepper-step>
        <v-stepper-content step='2'>
            <v-card 
                flat
            >
                <CalculatorStructureStep 
                    :items='typologies' 
                    :gia='Number(projInfo.gia)' 
                    @struct-out='updateStruct' 
                />
            </v-card>
            <v-card flat>
                <v-card-actions>
                    <v-btn text color='secondary' @click='stp = 3'>Continue</v-btn>
                    <v-btn text color='secondary' @click='stp = 1'>Back</v-btn>
                </v-card-actions>
            </v-card>
        </v-stepper-content>

        <v-stepper-step :complete='stp > 3' step='3' color='secondary' dark editable>
            Building Envelope
        </v-stepper-step>
        <v-stepper-content step='3'>
            <calculator-facade-step 
                :items='typologies' 
                :gia='Number(projInfo.gia)' 
                @env-out='updateEnv'
            />
            <v-card flat>
                <v-card-actions>
                    <v-btn text color='secondary' @click='stp = 4'>Continue</v-btn>
                    <v-btn text color='secondary' @click='stp = 2'>Back</v-btn>
                </v-card-actions>
            </v-card>
        </v-stepper-content>

        <v-stepper-step :complete='stp > 4' step='4' color='secondary' dark editable>
            Building Services
        </v-stepper-step>
        <v-stepper-content step='4'>
            <v-card height='200px' flat>
                <v-card-text>
                    <v-row>
                        <v-col cols='6'>
                            <p>
                                COMING SOON!! We're working away in the background to develop some MEP content.
                                In the future you'll be able to add Building services systems too.
                            </p>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-card flat>
                <v-card-actions>
                    <v-btn text color='secondary' @click='stp = 5'>Continue</v-btn>
                    <v-btn text color='secondary' @click='stp = 3'>Back</v-btn>
                </v-card-actions>
            </v-card>
        </v-stepper-content>

        <v-stepper-step :complete='stp > 5' step='5' color='secondary' dark editable>
            Summary
        </v-stepper-step>
        <v-stepper-content step='5'>
            <v-card height='100%'>
                <v-row dense>
                    <v-col cols='12'>
                        <v-card flat outlined class='mx-2'>
                            <v-row class='align-start' dense>
                                <v-col cols='6'>
                                    <v-card-title class='headline'>A1-5 Carbon Target: {{ projInfo.name }}</v-card-title>
                                    <v-card-text>
                                        
                                        <span class='secondary--text'>{{ projInfo.sector }} - {{ projInfo.type }}</span><br>
                                        Gross Internal Area: {{ projInfo.gia }} m<sup>2</sup>
                                    </v-card-text>
                                </v-col>
                                <v-col cols='6'>
                                    <v-card-title class='headline justify-end'>{{ projInfo.jobNumber }}</v-card-title>
                                </v-col>
                                <v-col cols='12'>
                                    <v-divider></v-divider>
                                </v-col>
                            </v-row>
                            <v-row dense>
                                <v-col cols='12'>
                                    <v-card-title class='headline'>Summary</v-card-title>
                                    <v-card-text>
                                        Total A1-5 Embodied Carbon: <strong>{{ allTotal }} tCO<sub>2</sub></strong><br>
                                        A1-5 Embodied Carbon Intensity: <strong>{{ allIntensity }} kgCO<sub>2</sub>/m<sup>2</sup><sub>GIA</sub></strong>
                                    </v-card-text>
                                </v-col>
                                <v-col cols='4' sm='6' md='4'>
                                    <v-card flat class='mx-auto'>
                                        <v-card-title class='justify-center'>Discipline Breakdown</v-card-title>
                                        <v-card-actions>
                                            <ve-ring
                                                :data='disChartData'
                                                :settings='disChartSettings'
                                                :colors='discColours'
                                                width='100%'
                                            ></ve-ring>
                                        </v-card-actions>
                                        
                                    </v-card>
                                </v-col>
                                <v-col cols='4' sm='6' md='4'>
                                    <v-card flat class='mx-auto'>
                                        <v-card-title class='justify-center'>Structure</v-card-title>
                                        <v-card-actions>
                                            <ve-ring
                                            :data='structChartData'
                                            :settings='structChartSettings'
                                            :colors='structColours'
                                            width='100%'                                            
                                        ></ve-ring>
                                        </v-card-actions>
                                    </v-card>
                                </v-col>
                                <v-col cols='4'>
                                    <v-card flat class='mx-auto'>
                                        <v-card-title class='justify-center'>Building Envelope</v-card-title>
                                        <ve-ring
                                            :data='envChartData'
                                            :settings='envChartSettings'
                                            :colors='envColours'
                                            width='100%'
                                            
                                        ></ve-ring>
                                    </v-card>
                                </v-col>


                                <v-col cols='12'>
                                    <v-divider></v-divider>
                                </v-col>
                            </v-row>
                            <v-row dense>
                                <v-col cols='12'>
                                    <v-card-title class='headline'>Building Structure</v-card-title>
                                </v-col>
                                <v-col cols='8'>
                                    <v-card flat class='mx-2'>
                                        <v-list dense>
                                            <v-list-item>
                                                <v-row dense>
                                                    <v-col cols='4'>
                                                        <v-list-item-content>
                                                            <strong>Type</strong>
                                                        </v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='text-center'>
                                                            <strong>kgCO<sub>2</sub>/m<sup>2</sup><sub>GIA</sub></strong>
                                                        </v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='text-center'>
                                                            <strong>tCO<sub>2</sub></strong>
                                                        </v-list-item-content>
                                                    </v-col>
                                                </v-row>
                                            </v-list-item>
                                            <v-list-item
                                                v-for='item in structByType'
                                                :key="item['type']"
                                            >
                                                <v-row dense>
                                                    <v-col cols='4'>
                                                        <v-list-item-content>
                                                            {{ item.type }}
                                                        </v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='justify-center'>
                                                            {{ item.carbonIntensity }}
                                                        </v-list-item-content>
                                                    </v-col>
                                                    <v-col>
                                                        <v-list-item-content class='justify-center'>
                                                            {{ item.carbon }}
                                                        </v-list-item-content>
                                                    </v-col>
                                                </v-row>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-row dense>
                                                    <v-col cols='4'>
                                                        <v-list-item-content><strong>TOTALS</strong></v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='text-center'><strong>{{ structIntensity }}</strong></v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='text-center'><strong>{{ structTotal }}</strong></v-list-item-content>
                                                    </v-col>
                                                </v-row>
                                            </v-list-item>
                                        </v-list>
                                    </v-card>
                                    <v-card flat>
                                        <v-card-text>
                                            <p>
                                                * a 10% allowance has been added to allow for items omitted from the original assessment. These 
                                                include fixtures and fixings not typically included within design up to the point of construction. 
                                                For a list of omissions, see the design assumptions within each typology card.
                                            </p>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                                <v-col cols='4'>
                                    <v-card flat class='mx-auto'>
                                        <ve-ring
                                            :data='structChartData'
                                            :settings='structChartSettings'
                                            :colors='structColours'
                                            width='90%'
                                            
                                        ></ve-ring>
                                    </v-card>
                                </v-col>
                                <v-col cols='12'>
                                    <v-divider></v-divider>
                                </v-col>
                            </v-row>

                            <v-row dense>
                                <v-col cols='12'>
                                    <v-card-title class='headline'>Building Envelope</v-card-title>
                                </v-col>
                                <v-col cols='8'>
                                    <v-card flat class='mx-2'>
                                        <v-list dense>
                                            <v-list-item>
                                                <v-row dense>
                                                    <v-col cols='4'>
                                                        <v-list-item-content>
                                                            <strong>Type</strong>
                                                        </v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='text-center'>
                                                            <strong>kgCO<sub>2</sub>/m<sup>2</sup><sub>GIA</sub></strong>
                                                        </v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='text-center'>
                                                            <strong>tCO<sub>2</sub></strong>
                                                        </v-list-item-content>
                                                    </v-col>
                                                </v-row>
                                            </v-list-item>
                                            <v-list-item
                                                v-for='item in envByType'
                                                :key="item['type']"
                                            >
                                                <v-row dense>
                                                    <v-col cols='4'>
                                                        <v-list-item-content>
                                                            {{ item.type }}
                                                        </v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='justify-center'>
                                                            {{ item.carbonIntensity }}
                                                        </v-list-item-content>
                                                    </v-col>
                                                    <v-col>
                                                        <v-list-item-content class='justify-center'>
                                                            {{ item.carbon }}
                                                        </v-list-item-content>
                                                    </v-col>
                                                </v-row>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-row dense>
                                                    <v-col cols='4'>
                                                        <v-list-item-content><strong>TOTALS</strong></v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='text-center'><strong>{{ envIntensity }}</strong></v-list-item-content>
                                                    </v-col>
                                                    <v-col cols='4'>
                                                        <v-list-item-content class='text-center'><strong>{{ envTotal }}</strong></v-list-item-content>
                                                    </v-col>
                                                </v-row>
                                            </v-list-item>
                                        </v-list>
                                    </v-card>
                                    <v-card flat>
                                        <v-card-text>
                                            <p>
                                                * a 10% allowance has been added to allow for items omitted from the original assessment. These 
                                                include fixtures and fixings not typically included within design up to the point of construction. 
                                                For a list of omissions, see the design assumptions within each typology card.
                                            </p>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                                <v-col cols='4'>
                                    <v-card flat class='mx-auto'>
                                        <ve-ring
                                            :data='envChartData'
                                            :settings='envChartSettings'
                                            :colors='envColours'
                                            width='90%'
                                            
                                        ></ve-ring>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-card>
                    </v-col>
                    <v-col cols='9'>
            
                    </v-col>
                </v-row>
            </v-card>
            <v-card flat>
                <v-card-actions>
                    <!-- @click to be added to print -->
                    <!-- <v-btn text color='secondary' >Print</v-btn> -->
                    <v-btn text color='secondary' @click='stp = 4'>Back</v-btn>
                </v-card-actions>
            </v-card>
        </v-stepper-content>
    </v-stepper>
    
</template>

<script>
import typologies from '@/store/typologies-buildings'
import CalculatorStructureStep from '@/components/CalculatorStructureStep'
import CalculatorFacadeStep from '@/components/CalculatorFacadeStep'

export default {
    components: {
        CalculatorStructureStep,
        CalculatorFacadeStep
    },

    data () {

        this.structChartSettings = {
            roseType: 'radius'
        }

        this.envChartSettings = {
            roseType: 'radius'
        }

        this.disChartSettings = {
            roseType: 'radius'
        }

        this.elemChartSettings = {
            roseType: 'radius'
        }
        
        return {
            structColours: ['#a5003a', '#cc133e', '#e33958', '#eb5b74', '#f2899b'],
            envColours: ['#005761', '#008ca5', '#0db0d2', '#4bc6e2', '#b1e7f4'],
            allColours: ['#008152', '#00a56b', '#46c08b', '#99d9ba', '#c1e8d5', '#f9a825', '#fdd835', '#fceb55', '#fff59d', '#fffde7', '#6b00a5', '#9205b3', '#af3bc4', '#d18fdc', '#f4e4f6'],
            otherColours: ['#6b00a5', '#9205b3', '#af3bc4', '#d18fdc', '#f4e4f6'],
            discColours:[ '#a5003a', '#005761', '#6b00a5' ],

            dialog: false,

            typologies,

            stp: 0,

            rules: {
                isNumber: v => Number.isInteger(Number(v)) || "The value must be an integer number",
                jn: v => v.length == 6 || "Job number must be 6 digits",
                aboveZero: v => v > 0 || "Must be greater than zero",
                exists: v => !!v || "This field is required"
            },

            sectors: [
                // 'Arts & Culture',
                // 'Aviation',
                'Commercial',
                // 'Energy',
                // 'Education',
                // 'Healthcare',
                // 'Highways',
                // 'Hotels & Leisure',
                // 'Infrastructure',
                // 'Rail',
                // 'Residential',
                // 'Retail',
                // 'Science & Industry',
                // 'Sport',
                // 'Water' 
            ],

            projectTypes: [
                'New Build',
                'Refurbishment',
                'Redevelopment'
            ],

            shapes: [
                'Rectangular',
                'L-shaped'
            ],

            grids: [
                '9x9',
                '12x9'
            ],

            projInfo: {
                name: '',
                jobNumber: '',
                sector: '',
                type: '',
                gia: 0,
            },

            structCarb: [],
            structByType: [],
            structTotal: 0,
            structIntensity: 0,

            structChartData: {
                columns: [
                    'type',
                    'carbon',
                ],
                rows: []
            },


            envCarb: [],
            envByType: [],
            envTotal: 0,
            envIntensity: 0,

            envChartData: {
                columns: [
                    'type',
                    'carbon',
                ],
                rows: []
            },

            allTotal: 0,
            allIntensity: 0,

            allCarb: {
                structures: [
                    {'type': 'none', 'carbon': 0, 'carbonIntensity': 0}
                ],

                envelope: [
                    {'type': 'none', 'carbon': 0, 'carbonIntensity': 0}
                ],

                mep: [
                    {'type': 'none', 'carbon': 0, 'carbonIntensity': 0}
                ]
            },

            disChartData: {
                columns: [
                    'type',
                    'carbon',
                ],
                rows: []
            },

            elemChartData: {
                columns: [
                    'type',
                    'carbon',
                ],
                rows: []
            },
            
        }
    },

    computed: {

    },

    methods: {
        updateStruct (structItems) {
            
            this.structCarb = structItems
            console.log(this.structCarb)

        },

        updateEnv (envItems) {
            
            this.envCarb = envItems
            console.log(this.envCarb)
        },

        
    },

    watch: {

        'structCarb' : {
            handler: function () {
                var items = this.structCarb
                var gia = this.projInfo.gia

                if ( gia == 0 ) {
                    gia = 1
                }

                // get types used
                var rawTypes = []

                for (var i=0; i < items.length; i++) {
                    rawTypes.push(items[i]['subCategory'])
                }
                var uniqueTypes = Array.from(new Set(rawTypes))

                // get types contribution
                var typesCarbon = []
                var totalCarbon = 0
                var relCarbon = 0

                for (var j=0; j < uniqueTypes.length; j++) {
                    var type = uniqueTypes[j]
                    var carb = 0
                    

                    for (var x=0; x < items.length; x++) {
                        var item = items[x]

                        if (item['subCategory'] == type) {
                            carb += item['absCarbon']
                        }
                    }

                    var carbInt = Math.round(10*(carb / gia))/10

                    // convert to tCO2 to 1 dp
                    carb = Math.round(carb/1000)

                    typesCarbon.push({
                        'type': type,
                        'carbon': carb,
                        'carbonIntensity': carbInt
                    })

                    totalCarbon += carb
                    relCarbon += carbInt
                    
                }

                typesCarbon.push({
                    'type': '10% allowance (structure)',
                    'carbon': Math.round(10*totalCarbon*0.1)/10,
                    'carbonIntensity': Math.round(100*relCarbon*0.1)/100,
                })

                totalCarbon += Math.round(10*totalCarbon*0.1)/10
                relCarbon += Math.round(100*relCarbon*0.1)/100

                //console.log(typesCarbon)

                this.structByType = typesCarbon
                this.structTotal = Math.round(10*totalCarbon)/10
                this.structIntensity = Math.round(100*relCarbon)/100

                var data = typesCarbon
                //console.log(data)
                this.structChartData.rows = data
                
                this.allCarb.structures = data

            },
            deep: true
        },
        
        'envCarb' : {
            handler: function () {
                var items = this.envCarb
                var gia = this.projInfo.gia

                if ( gia == 0 ) {
                    gia = 1
                }

                // get types used
                var rawTypes = []

                for (var i=0; i < items.length; i++) {
                    rawTypes.push(items[i]['subCategory'])
                }
                var uniqueTypes = Array.from(new Set(rawTypes))

                // get types contribution
                var typesCarbon = []
                var totalCarbon = 0
                var relCarbon = 0

                for (var j=0; j < uniqueTypes.length; j++) {
                    var type = uniqueTypes[j]
                    var carb = 0
                    

                    for (var x=0; x < items.length; x++) {
                        var item = items[x]

                        if (item['subCategory'] == type) {
                            carb += item['absCarbon']
                        }
                    }

                    var carbInt = Math.round(100*(carb / gia))/100

                    // convert to tCO2 to 1 dp
                    carb = Math.round(10*(carb/1000))/10

                    typesCarbon.push({
                        'type': type,
                        'carbon': carb,
                        'carbonIntensity': carbInt
                    })

                    totalCarbon += carb
                    relCarbon += carbInt
                    
                }


                // add 10% allowance
                typesCarbon.push({
                    'type': '10% allowance (envelope)',
                    'carbon': Math.round(10*(totalCarbon*0.1))/10,
                    'carbonIntensity': Math.round(100*(relCarbon*0.1))/100,
                })

                totalCarbon += Math.round(10*(totalCarbon*0.1))/10
                relCarbon += Math.round(100*(relCarbon*0.1))/100

                //console.log(typesCarbon)

                this.envByType = typesCarbon
                this.envTotal = Math.round(10*totalCarbon)/10
                this.envIntensity = Math.round(100*relCarbon)/100

                var data = typesCarbon
                //console.log(data)
                this.envChartData.rows = data 

                this.allCarb.envelope = data

            },
            deep: true
        },

        'allCarb': {
            handler: function () {
                const structData = this.allCarb['structures']
                const envData = this.allCarb['envelope']

                var allData = structData.concat(envData)

                // get total a1-5 carbon
                this.allTotal = Math.round(this.structTotal + this.envTotal)
                this.allIntensity = Math.round(10*(this.structIntensity + this.envIntensity))/10

                this.elemChartData.rows = allData

                // create data for by discipline breakdown
                var structSum = { 'type': 'Structure', 'carbon': 0, 'carbonIntensity': 0 }
                var envSum = { 'type': 'Envelope', 'carbon': 0, 'carbonIntensity': 0 }
                var mepSum = { 'type': 'Services', 'carbon': 0, 'carbonIntensity': 0 }

                for (var i=0; i < structData.length; i++) {
                    let structItem = structData[i]

                    structSum['carbon'] += structItem['carbon']
                    structSum['carbonIntensity'] += structItem['carbonIntensity']
                }

                for (var j=0; j < envData.length; j++) {
                    let envItem = envData[j]

                    envSum['carbon'] += envItem['carbon']
                    envSum['carbonIntensity'] += envItem['carbonIntensity']
                }

                var disBreakdown = [
                    structSum,
                    envSum,
                    mepSum,
                ]

                console.log(disBreakdown)

                this.disChartData.rows = disBreakdown

            },
            deep: true
        }
    }
}
</script>